import { WarningCircleIcon } from '@phosphor-icons/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  disputeReasonKey,
  feeBenefitKey,
  feeModelKey,
  feeStatusKey,
  useDisputeSuccessFee,
  useSuccessFee,
  type SuccessFeeDisputeReason,
} from '@entities/success-fee';
import { money } from '@shared/lib/money';
import { percentFromBps } from '@shared/lib/percent';
import { Badge, Button } from '@shared/ui';

import styles from './Handover.module.css';

interface SuccessFeeCardProps {
  searchId: string;
  /** Исход отмечен — до отметки расчёта не существует (§42.4). */
  outcomeRecorded: boolean;
}

/** Порядок причин — порядок §42.5. Свободного текста нет: разбор вне системы. */
const DISPUTE_REASONS: SuccessFeeDisputeReason[] = [
  'PRICE_DIFFERS',
  'DISCOUNT_BEFORE_CARVIQ',
  'BENEFIT_NOT_RECEIVED',
  'PURCHASE_DID_NOT_HAPPEN',
  'OTHER',
];

/**
 * Вознаграждение по результату (FE-330, MVP_PRD §42.4, §42.5).
 *
 * Пять правил блока:
 *
 * * **сумма приходит с расшифровкой, а не одна.** Обе цены с основаниями,
 *   разница, ставка и итог — либо перечень позиций с цитатами: счёт без
 *   объяснения непроверяем;
 * * **позиция существует только с цитатой и без денежной оценки** (§42.1):
 *   стоимость подарков CarViQ.DE не оценивает и здесь не показывает;
 * * **«не списывается системой» сказано прямо** (§42.6). Провайдер — заглушка,
 *   счёт выставляется вне системы, и обещать списание нельзя;
 * * **спор — одна кнопка с причиной из закрытого списка**; после него сумма не
 *   считается подлежащей оплате, и это видно на экране;
 * * **до отметки исхода блока нет вовсе.** Показывать ноль до покупки значило
 *   бы обещать расчёт, которого никто не делал.
 */
export function SuccessFeeCard({ searchId, outcomeRecorded }: SuccessFeeCardProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'de';
  const query = useSuccessFee(searchId, outcomeRecorded);
  const dispute = useDisputeSuccessFee(searchId);
  const [disputing, setDisputing] = useState(false);

  const fee = query.data;
  if (!outcomeRecorded || fee === undefined) return null;

  const disputed = fee.status === 'disputed';

  return (
    <section className={styles.section} aria-labelledby="handover-fee">
      <h2 id="handover-fee" className={styles.title}>
        {t('successFee.title')}
      </h2>
      {/* Ставка — из строки расчёта, а не из конфигурации: смена
          `SUCCESS_FEE_RATE_BPS` уже посчитанное не переписывает (FE-583). */}
      <p className={styles.subtitle}>
        {t(feeModelKey(fee.model), {
          rate: fee.rate_bps === null ? undefined : percentFromBps(fee.rate_bps, locale),
        })}
      </p>

      <p className={styles.feeAmount}>{money(fee.amount_cents, fee.currency, locale)}</p>
      <p className={styles.subtitle}>
        {/* Спор — «требует внимания» с иконкой; прочие статусы — нейтральная
            метка: цвет не единственный носитель смысла (DESIGN_SYSTEM §5.8). */}
        <Badge
          tone={disputed ? 'attention' : 'neutral'}
          icon={disputed ? <WarningCircleIcon weight="bold" /> : undefined}
        >
          {t(feeStatusKey(fee.status))}
        </Badge>
      </p>

      {/* Расшифровка: обе цены с основаниями, разница и ставка (§42.4). */}
      <dl className={styles.facts}>
        {fee.baseline_price_cents === null ? null : (
          <div>
            <dt>{t('successFee.baseline')}</dt>
            <dd className="tabular">
              {money(fee.baseline_price_cents, fee.currency, locale, 'auto')}
            </dd>
          </div>
        )}
        {fee.final_price_cents === null ? null : (
          <div>
            <dt>{t('successFee.finalPrice')}</dt>
            <dd className="tabular">
              {money(fee.final_price_cents, fee.currency, locale, 'auto')}
            </dd>
          </div>
        )}
        {fee.discount_cents === null ? null : (
          <div>
            <dt>{t('successFee.discount')}</dt>
            <dd className="tabular">{money(fee.discount_cents, fee.currency, locale, 'auto')}</dd>
          </div>
        )}
        {fee.rate_bps === null ? null : (
          <div>
            <dt>{t('successFee.rate')}</dt>
            {/* Ставка не округляется: 25,5 % — это 25,5 %, а не «26 %». */}
            <dd className="tabular">{percentFromBps(fee.rate_bps, locale)} %</dd>
          </div>
        )}
      </dl>

      {/* Основание стартовой цены — цитата автохауса, если она есть (§42.3). */}
      {typeof fee.baseline_evidence.quote === 'string' ? (
        <blockquote className={styles.quote}>«{fee.baseline_evidence.quote}»</blockquote>
      ) : null}

      {fee.benefits.length === 0 ? null : (
        <ul className={styles.quotes}>
          {fee.benefits.map((item) => (
            <li key={item.code}>
              <span className={styles.label}>{t(feeBenefitKey(item.code))}</span>
              <blockquote className={styles.quote}>«{item.quote}»</blockquote>
            </li>
          ))}
        </ul>
      )}

      <p className={styles.note}>{t('successFee.notCharged')}</p>
      {fee.dealer_confirmed_at === null ? (
        <p className={styles.note}>{t('successFee.noDealerConfirmation')}</p>
      ) : null}

      {disputed ? (
        <p className={styles.warning}>
          {t('successFee.disputed', {
            reason: t(disputeReasonKey(fee.dispute_reason ?? 'OTHER')),
          })}
        </p>
      ) : disputing ? (
        <div className={styles.disputeChoice} role="group" aria-live="polite">
          <p className={styles.confirmTitle}>{t('successFee.disputeTitle')}</p>
          {DISPUTE_REASONS.map((reason) => (
            <Button
              key={reason}
              variant="secondary"
              disabled={dispute.isPending}
              onClick={() => {
                dispute.mutate(reason);
              }}
            >
              {t(disputeReasonKey(reason))}
            </Button>
          ))}
          <Button
            variant="ghost"
            disabled={dispute.isPending}
            onClick={() => {
              setDisputing(false);
            }}
          >
            {t('common.cancel')}
          </Button>
        </div>
      ) : (
        <div className={styles.actions}>
          <Button
            variant="secondary"
            onClick={() => {
              setDisputing(true);
            }}
          >
            {t('successFee.dispute')}
          </Button>
        </div>
      )}
    </section>
  );
}
